import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Cropper from "react-easy-crop";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Camera, X, Check, ArrowLeft, KeyRound, Save, Loader2,
  Twitter, Instagram, Linkedin, Globe, Trash2, CheckCircle2, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Utility: build a canvas and produce the cropped 1:1 JPEG
async function getCroppedBlob(src, crop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
  const canvas = document.createElement("canvas");
  // Output at 512x512 regardless of source (keeps it 1:1 and reasonable size)
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x, crop.y, crop.width, crop.height,
    0, 0, 512, 512,
  );
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
  });
}

export default function AdminProfile({ portal = "admin" }) {
  const isStaffPortal = portal === "staff";
  const dashboardPath = isStaffPortal ? "/staff" : "/admin";
  const loginPath = isStaffPortal ? "/staff/login" : "/admin/login";
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    display_name: "", bio: "", date_of_birth: "",
    twitter: "", instagram: "", linkedin: "", website: "",
  });
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  // Avatar crop modal
  const fileInputRef = useRef();
  const [rawImage, setRawImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Password modal
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdErr, setPwdErr] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("koko_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/profile`, auth);
      setProfile(data);
      const s = data.socials || {};
      setForm({
        display_name: data.display_name || "",
        bio: data.bio || "",
        date_of_birth: data.date_of_birth || "",
        twitter: s.twitter || "",
        instagram: s.instagram || "",
        linkedin: s.linkedin || "",
        website: s.website || "",
      });
    } catch (e) {
      if (e.response?.status === 401) navigate(loginPath);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!token) { navigate(loginPath); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const showFlash = (msg, good = true) => {
    setFlash({ msg, good });
    setTimeout(() => setFlash(null), 3500);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/me/profile`, {
        display_name: form.display_name,
        bio: form.bio,
        date_of_birth: form.date_of_birth,
        socials: {
          twitter: form.twitter, instagram: form.instagram,
          linkedin: form.linkedin, website: form.website,
        },
      }, auth);
      showFlash("Profile saved");
      await load();
    } catch (e) {
      showFlash(e.response?.data?.detail || "Save failed", false);
    }
    setSaving(false);
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const uploadAvatar = async () => {
    if (!rawImage || !croppedPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(rawImage, croppedPixels);
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      await axios.post(`${API}/me/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setRawImage(null); setCroppedPixels(null); setZoom(1); setCrop({ x: 0, y: 0 });
      showFlash("Avatar updated");
      await load();
    } catch (e) {
      showFlash(e.response?.data?.detail || "Upload failed", false);
    }
    setUploading(false);
  };

  const removeAvatar = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    try {
      await axios.delete(`${API}/me/avatar`, auth);
      showFlash("Avatar removed");
      await load();
    } catch (e) {
      showFlash(e.response?.data?.detail || "Failed", false);
    }
  };

  const changePassword = async () => {
    setPwdErr("");
    if (pwdForm.new_password.length < 6) { setPwdErr("New password must be 6+ chars"); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { setPwdErr("Passwords do not match"); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API}/me/password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      }, auth);
      setPwdOpen(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
      showFlash("Password updated");
    } catch (e) {
      setPwdErr(e.response?.data?.detail || "Failed to change password");
    }
    setPwdSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading profile…
        </div>
      </div>
    );
  }
  if (!profile) return null;

  const avatarSrc = profile.avatar_url ? `${BACKEND}${profile.avatar_url}` : null;
  const initial = (profile.display_name || profile.name || "?").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-profile-page">
      <Helmet>
        <title>Profile — KoKo Sites</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to={dashboardPath} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-2">
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 border ${profile.role === "admin" ? "text-red-400 border-red-600/40 bg-red-600/10" : "text-zinc-400 border-white/10 bg-white/5"}`}>
            {profile.role}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10 space-y-6">
        {flash && (
          <div className={`border-2 px-4 py-3 flex items-center gap-3 ${flash.good ? "border-green-500/50 bg-green-500/10 text-green-400" : "border-red-600/50 bg-red-600/10 text-red-400"}`}>
            {flash.good ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <span className="text-sm">{flash.msg}</span>
          </div>
        )}

        {/* Header: avatar + identity */}
        <div className="border border-white/10 bg-gradient-to-b from-[#141414] to-[#0E0E0E] p-5 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 text-center sm:text-left">
          <div className="relative group shrink-0">
            <div className="w-36 h-36 sm:w-32 sm:h-32 border-2 border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
              {avatarSrc ? (
                <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl font-black text-white">{initial}</span>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="avatar-upload-btn"
              className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity text-white"
            >
              <Camera size={22} />
              <span className="text-[10px] uppercase tracking-widest mt-2">Change</span>
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={onPickFile}
              className="hidden"
              data-testid="avatar-file-input"
            />
            {profile.has_avatar && (
              <button
                onClick={removeAvatar}
                data-testid="avatar-remove-btn"
                className="absolute -top-2 -right-2 w-7 h-7 bg-red-600 border-2 border-black text-white flex items-center justify-center hover:bg-red-500"
                title="Remove avatar"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black uppercase tracking-tight">{profile.display_name || profile.name}</h1>
            <p className="text-sm text-zinc-500 mt-1">{profile.email}</p>
            {profile.bio && <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{profile.bio}</p>}
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-4 text-[11px] text-zinc-500">
              {profile.tags?.length > 0 && profile.tags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                  style={{ backgroundColor: t.color + "25", borderLeft: `2px solid ${t.color}` }}
                >
                  {t.name}
                </span>
              ))}
              {profile.age != null && (
                <span className="border border-white/10 px-2 py-1 uppercase tracking-widest">Age {profile.age}</span>
              )}
              {profile.socials?.twitter && <a className="border border-white/10 px-2 py-1 hover:border-red-600 hover:text-red-400 flex items-center gap-1" href={profile.socials.twitter} target="_blank" rel="noopener noreferrer"><Twitter size={10} /> Twitter</a>}
              {profile.socials?.instagram && <a className="border border-white/10 px-2 py-1 hover:border-red-600 hover:text-red-400 flex items-center gap-1" href={profile.socials.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={10} /> Instagram</a>}
              {profile.socials?.linkedin && <a className="border border-white/10 px-2 py-1 hover:border-red-600 hover:text-red-400 flex items-center gap-1" href={profile.socials.linkedin} target="_blank" rel="noopener noreferrer"><Linkedin size={10} /> LinkedIn</a>}
              {profile.socials?.website && <a className="border border-white/10 px-2 py-1 hover:border-red-600 hover:text-red-400 flex items-center gap-1" href={profile.socials.website} target="_blank" rel="noopener noreferrer"><Globe size={10} /> Website</a>}
            </div>
          </div>
        </div>

        {/* Edit form */}
        <div className="border border-white/10 bg-[#0E0E0E] p-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white mb-5">Personal Info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Display Name</Label>
              <Input data-testid="profile-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Date of Birth</Label>
              <Input data-testid="profile-dob" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Bio <span className="text-zinc-700">({form.bio.length}/500)</span></Label>
              <Textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 500) })} rows={3} placeholder="Tell people a bit about you…" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 resize-none" />
            </div>
          </div>

          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white mt-8 mb-5">Socials</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <SocialInput icon={Twitter} label="Twitter / X" value={form.twitter} onChange={(v) => setForm({ ...form, twitter: v })} testid="profile-twitter" placeholder="https://x.com/username" />
            <SocialInput icon={Instagram} label="Instagram" value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} testid="profile-instagram" placeholder="https://instagram.com/username" />
            <SocialInput icon={Linkedin} label="LinkedIn" value={form.linkedin} onChange={(v) => setForm({ ...form, linkedin: v })} testid="profile-linkedin" placeholder="https://linkedin.com/in/username" />
            <SocialInput icon={Globe} label="Personal Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="profile-website" placeholder="https://yoursite.com" />
          </div>

          <div className="flex items-center gap-3 mt-8 pt-5 border-t border-white/5">
            <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs disabled:opacity-50 flex items-center gap-2">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save Changes</>}
            </button>
            <button onClick={() => setPwdOpen(true)} data-testid="open-change-pwd-btn" className="border-2 border-white/20 text-white font-bold uppercase tracking-widest py-3 px-6 hover:border-white transition-colors text-xs flex items-center gap-2">
              <KeyRound size={14} /> Change Password
            </button>
          </div>
        </div>

        {/* Permissions section (read-only for self) */}
        <div className="border border-white/10 bg-[#0E0E0E] p-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white mb-2">Your Access</h2>
          <p className="text-[11px] text-zinc-500 mb-5">These are the features you can use. {profile.role === "admin" ? "Admin — full access." : "Only the admin can toggle these for you."}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {["reviews", "contacts", "payments", "ai_email", "email_health", "team"].map((key) => {
              const enabled = profile.permissions?.[key];
              return (
                <div key={key} className={`border px-3 py-2.5 flex items-center gap-2 ${enabled ? "border-green-500/30 bg-green-500/5 text-green-400" : "border-white/10 bg-black/20 text-zinc-600"}`}>
                  {enabled ? <Check size={14} /> : <X size={14} />}
                  <span className="text-[11px] uppercase tracking-wider font-bold">{key.replace("_", " ")}</span>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Avatar crop modal */}
      {rawImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="avatar-crop-modal">
          <div className="border border-white/10 bg-[#0A0A0A] w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h3 className="text-xs font-black uppercase tracking-[0.18em] text-white">Crop to Square (1:1)</h3>
              <button onClick={() => { setRawImage(null); setZoom(1); setCrop({ x: 0, y: 0 }); }} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="relative bg-black" style={{ height: 380 }}>
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="px-5 py-4 border-t border-white/10 space-y-3">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500">
                Zoom
                <input
                  type="range" min={1} max={3} step={0.05} value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full mt-1 accent-red-600"
                  data-testid="avatar-zoom"
                />
              </label>
              <button
                onClick={uploadAvatar}
                disabled={uploading}
                data-testid="avatar-upload-submit"
                className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Check size={14} /> Save Avatar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change password modal */}
      {pwdOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPwdOpen(false)}>
          <div className="border border-white/10 bg-[#0A0A0A] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">Change Password</h3>
              <button onClick={() => setPwdOpen(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Current Password</Label>
                <Input data-testid="change-pwd-current" type="password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">New Password</Label>
                <Input data-testid="change-pwd-new" type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Confirm New Password</Label>
                <Input data-testid="change-pwd-confirm" type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
              </div>
              {pwdErr && <div className="bg-red-600/10 border border-red-600/40 px-3 py-2 text-xs text-red-400">{pwdErr}</div>}
              <button onClick={changePassword} disabled={pwdSaving} data-testid="change-pwd-submit" className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs disabled:opacity-50 flex items-center justify-center gap-2">
                {pwdSaving ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : <><KeyRound size={14} /> Update Password</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SocialInput({ icon: Icon, label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Icon size={11} /> {label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
    </div>
  );
}
